import React from 'react'
import { Formik, FormikHelpers } from 'formik'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import * as Yup from 'yup'
import type { StringSchema } from 'yup'
import { useDispatch } from 'react-redux'
import { Dispatch } from '@/store'
import { createModal, useModalize, AdditionalModalProps } from '@/components/modalize/mui'
import { breakpoints } from '@/styles'
import { PaymentMethod } from '@/pages/Settings/PlanSettings/use-payment-methods'
import useTaxCode from '@/hooks/use-tax-code'
import BillingInfoForm from './form'
import { FormValues } from './types'
import { showConfirmModal } from '../confirm-payment-modal'

const ContentWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 32px 16px;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    padding: 24px 24px 8px;
  }
`

const ContentInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 685px;
  width: 100%;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    width: unset;
  }
`

type Props = AdditionalModalProps & {
  taxFlag?: boolean
  card?: PaymentMethod
}

const BillingInfoModal: React.FC<Props> = ({ ModalTitle, ModalFooter, taxFlag, card }) => {
  const { close } = useModalize()
  const { t } = useTranslation()
  const dispatch = useDispatch<Dispatch>()
  const paymentMethodId = card?.payment_method_id
  const hasPaymentMethod = !!paymentMethodId
  const { getValue: getTaxCode } = useTaxCode()

  const title = hasPaymentMethod
    ? t('l.plan_settings.edit.edit_payment_method.title', 'Edit Payment Method')
    : t('l.plan_settings.edit.add_payment_method.title', 'Add Payment Method')

  const initialValues: FormValues = {
    default_payment_method: true,
    payment_method: 'stripe',
    first_name: card?.first_name ?? '',
    last_name: card?.last_name ?? '',
    street1: card?.street1 ?? '',
    street2: card?.street2 ?? '',
    city: card?.city ?? '',
    state: card?.state ?? '',
    country: 'ca',
    zip: card?.zip ?? '',
    card_number: '',
    expiration_date: card?.expiration_date ?? '',
    security_code: '',
    save_payment_method: true,
    save_billing_address: true,
    tax_code: card?.tax_code ?? ''
  }

  const validationSchema = Yup.object().shape({
    first_name: Yup.string().required(),
    last_name: Yup.string().required(),
    street1: Yup.string().required(),
    street2: Yup.string(),
    city: Yup.string().required(),
    state: Yup.string().required(),
    country: Yup.string().required(),
    zip: Yup.string().required(),
    card_number: Yup.string().required(),
    expiration_date: Yup.string().length(4).required(),
    security_code: Yup.string().min(3).max(4).required(),
    default_payment_method: Yup.boolean(),
    save_payment_method: Yup.boolean(),
    save_billing_address: Yup.boolean(),
    tax_code: Yup.string().when(['country', 'state'], ((
      country: string,
      state: string,
      schema: StringSchema
    ) => {
      const taxCode = getTaxCode(country, state)
      if ('format' in taxCode && taxCode.format) {
        return schema.matches(taxCode.format, taxCode.error)
      }
      return schema
    }) as any)
  })

  // Work around JS / Rematch 2nd arg as payload inference
  const updatePayment: (card: FormValues, paymentId?: string) => Promise<any> =
    dispatch.plans.updatePaymentMethod

  const onSubmit = async (
    values: FormValues,
    { setSubmitting }: Pick<FormikHelpers<FormValues>, 'setSubmitting'>
  ) => {
    setSubmitting(true)
    const { error } = await updatePayment(values, card?.payment_method_id)
    if (!error) {
      showConfirmModal({ type: 'edit' })
      close()
    }
    setSubmitting(false)
  }
  return (
    <ContentWrapper>
      <ModalTitle>{title}</ModalTitle>
      <ContentInner>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {({ values, isValid, isSubmitting, setFieldValue, setSubmitting }) => (
            <BillingInfoForm
              onSubmit={() => onSubmit(values, { setSubmitting })}
              values={values}
              isSubmitting={isSubmitting}
              setFieldValue={setFieldValue}
              isValid={isValid}
              close={close}
              card={card}
              taxFlag={taxFlag}
              ModalFooter={ModalFooter}
            />
          )}
        </Formik>
      </ContentInner>
    </ContentWrapper>
  )
}

export const showBillingInfoModal = createModal(BillingInfoModal, {
  width: '100%',
  maxWidth: '636px',
  overflow: 'var(--dropdown)',
  sheet: true
})
